import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
import { Banner, Button, Typography } from '@riac/design-system';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);

import { CatalogTextAlerts } from
  'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/CatalogTextAlerts/CatalogTextAlerts';
import { CatalogWishlists } from
  'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/CatalogWishlists/CatalogWishlists';
import { PlaceBid } from 'src/components/PlaceBid/PlaceBid';
import { formatCurrency } from 'src/lib/catalog';
import { LoginContext } from 'src/lib/LoginContext';
import { LoginToUse } from 'src/components/LoginToUse/LoginToUse';
import { BiddingContext } from 'src/lib/BiddingContext';
import { timedAuctionGetEndMessage } from 'src/components/timedAuctionGetEndMessage';
import { getIsPricelessLot } from 'src/lib/getIsPricelessLot';
import { getEndMessage } from 'src/components/AuctionInfo/TimedAuctionInfo';
import {
  getBidCountMessage,
} from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/AuctionItemActions';

interface IDetailPageActionArea {
  auctionId: number;
  itemInfo: any;
  primaryItem: any;
  allowBid: boolean;
}

export const DetailPageActionArea = ({
  itemInfo, primaryItem, allowBid,
}: IDetailPageActionArea): JSX.Element => {
  const endDate = dayjs.tz(primaryItem.EndDate, 'America/Chicago');
  const now = dayjs().tz('America/Chicago');
  const { isLoggedIn } = useContext(LoginContext);
  const [bidInCart, setBidInCart] = useState(false);
  const [timedAuctionLotActive, setTimedAuctionLotActive] = useState(endDate.isAfter(now));
  const { getMessageForLot } = useContext(BiddingContext);
  useEffect(() => {
    setBidInCart(false);
    const e = dayjs.tz(primaryItem.EndDate, 'America/Chicago');
    const n = dayjs().tz('America/Chicago');
    const diff = e.diff(n);
    let timer = null;
    if(e.isAfter(n) && diff > 0 && diff < 86400000) {
      setTimedAuctionLotActive(true);
      timer = setTimeout(() => {
        setTimedAuctionLotActive(false);
      }, diff+1000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [primaryItem.EndDate]);

  return (
    <div className='flex flex-col gap-5 w-full'>
      <div className='flex flex-col gap-5 p-5 w-full border-1 border-border-dark'>
        {(itemInfo?.PricesRealized && primaryItem?.PriceRealized)
          && <Banner size='sm' color='primary' className='w-full max-w-96' centerIconVertically>
            <div className='flex flex-row flex-wrap items-center justify-stretch w-full gap-0.5'>
              Price Realized:
              <LoginToUse overlayText={'Login to view'} hideChildren alwaysShowOverlay className='items-center justify-center flex'>
                {/* filters generated by https://codepen.io/sosuke/pen/Pjoqqp */}
                <img className='h-6' src={primaryItem?.PriceRealized} alt='Price obfuscated' style={{ filter: 'invert(29%) sepia(95%) saturate(8214%) hue-rotate(222deg) brightness(89%) contrast(95%)' }} />
              </LoginToUse>
            </div>
          </Banner>
          || ''}
        {(itemInfo?.AuctionTypeID !== TIMED_AUCTION_TYPE_ID)
          && <div className='flex flex-col gap-0'>
            <Typography color='text-lighter' weight='medium' align='center'>
              Estimated Price
            </Typography>
            <Typography color='text' heading size='lg' weight='extrabold' align='center'>
              {
                getIsPricelessLot(primaryItem?.AuctionID, primaryItem?.LotNumber) ? 'Priceless'
                  : (
                    (primaryItem?.LowEst && primaryItem?.HighEst)
                      ? `${formatCurrency(primaryItem?.LowEst, {trailingZeroDisplay: 'stripIfInteger'})} - ${formatCurrency(primaryItem?.HighEst, {trailingZeroDisplay: 'stripIfInteger'})}`
                      : 'Not Available'
                  )
              }
            </Typography>
          </div>
        }
        {allowBid && (<>
          {itemInfo.AuctionTypeID === TIMED_AUCTION_TYPE_ID
            && <div className='flex flex-col gap-1'>
              <div className='flex flex-col gap-5'>
                {(primaryItem.HighBidder && (
                  <div className='flex flex-col gap-1 w-full'>
                    <Banner color='success' size='sm' centeredTextWithIcon centerIconVertically>
                      You are the high bidder.
                    </Banner>
                  </div>
                )) || ((primaryItem.PreviousBid && isLoggedIn)
                  && <div className='flex flex-col gap-1 w-full'>
                    <Banner color='error' size='sm' centeredTextWithIcon centerIconVertically>
                      You have been outbid.
                    </Banner>
                  </div>
                || '')}
                {primaryItem.CurrentBid ? (
                  <div className='flex flex-row justify-between items-stretch'>
                    <div className='flex-1'>
                      <Typography color='text-lighter' weight='medium' align='center'>
                        Current Bid
                        <Typography color='text' heading size='lg' weight='extrabold' align='center'>
                          {formatCurrency(primaryItem.CurrentBid, {trailingZeroDisplay: 'stripIfInteger'})}
                        </Typography>
                        <Typography size={'sm'} align={'center'}>
                          {getBidCountMessage(primaryItem.StartingBid, primaryItem.CurrentBid)}
                        </Typography>
                      </Typography>
                    </div>
                    {primaryItem.HighBidder && (
                      <div className='flex-1 border-l-1 border-border-dark'>
                        <Typography color='text-lighter' weight='medium' align='center'>
                          Max Bid
                          <Typography color='text' heading size='lg' weight='extrabold' align='center'>
                            {formatCurrency(primaryItem.HighBidAmount, {trailingZeroDisplay: 'stripIfInteger'})}
                          </Typography>
                        </Typography>
                      </div>
                    )}
                  </div>
                ) : (
                  <Typography color='text-lighter' weight='medium' align='center'>
                    Starting Bid
                    <Typography color='text' heading size='lg' weight='extrabold' align='center'>
                      {formatCurrency(primaryItem.StartingBid, {trailingZeroDisplay: 'stripIfInteger'})}
                    </Typography>
                  </Typography>
                )}
                <Typography size='xs' color='text-light' align='center'>
                  {timedAuctionGetEndMessage(now, endDate)}
                </Typography>
              </div>
            </div>
          }
          <LoginToUse
            overlayText='Log In To Bid'
            className='w-full'
            childrenWrapperClassName='w-full flex flex-col gap-5'
          >
            <div className='flex flex-col flex-wrap gap-3 items-stretch'>
              {getMessageForLot(primaryItem.LotNumber)}
              <div className='flex flex-row flex-wrap gap-4 items-center'>
                {(itemInfo.AuctionTypeID !== TIMED_AUCTION_TYPE_ID || timedAuctionLotActive) && (
                  <PlaceBid
                    type='sealed'
                    auctionTypeID={itemInfo.AuctionTypeID}
                    item={primaryItem}
                    addBid={() => {itemInfo.AuctionTypeID !== TIMED_AUCTION_TYPE_ID && setBidInCart(true);}}
                    className='flex-1 min-w-36'
                    size='lg'
                  />
                )}
                {(itemInfo.AuctionTypeID === TIMED_AUCTION_TYPE_ID && timedAuctionLotActive) && (
                  <PlaceBid
                    type={'increment'}
                    item={primaryItem}
                    auctionTypeID={itemInfo.AuctionTypeID}
                    addBid={() => {}}
                    className='flex-1 min-w-36'
                    variant='secondary'
                    size='lg'
                  />
                )}
                {itemInfo.AuctionTypeID !== TIMED_AUCTION_TYPE_ID && (
                  <PlaceBid
                    type='phone'
                    auctionTypeID={itemInfo.AuctionTypeID}
                    item={primaryItem}
                    addBid={() => setBidInCart(true)}
                    className='flex-1 min-w-36'
                    variant='secondary'
                    size='md'
                  />
                )}
              </div>
              {(primaryItem.citem === 1 || bidInCart) && (
                <Button variant='text' className='text-sm leading-[22px] p-0 flex-1 place-self-center' to='/account/cart'>
                  View Bid in Cart
                </Button>
              )}
              {itemInfo.AuctionTypeID !== TIMED_AUCTION_TYPE_ID && primaryItem.sbitem === 1 && (
                <Button variant='text' className='text-sm leading-[22px] p-0 flex-1 place-self-center' to='/account/bids'>
                  View Submitted Bid
                </Button>
              )}
            </div>
          </LoginToUse>
        </>)}
      </div>
      <LoginToUse overlayText='Log In to use Wishlists' className='w-full'>
        <div className='flex flex-col items-center w-full gap-3'>
          <CatalogWishlists
            item={itemInfo.Items[0]}
            title={itemInfo.Title}
            inverted
            noOptionsBorder
          />
          {itemInfo.AuctionTypeID !== TIMED_AUCTION_TYPE_ID ? <CatalogTextAlerts item={itemInfo.Items[0]}/> : null}
        </div>
      </LoginToUse>
    </div>
  );
};
